import { inject, Injectable } from '@angular/core';
import { LanguageService } from '@uigy/shared/services/language.service';
import {
  AssetExpressionsParams,
  AssetField, PolicyFindingFilterField,
  FindingField,
  FindingsObjExpressionsParams,
  PluginSource, PolicyFindingsExpressionsParams,
  TimeFrame,
} from '@uigy/generated/graphql';
import { KpiSmallVariant, WhatsNewKpiConfig } from '@uigy/modules/overview/kpis/kpi.interfaces';
import { KpiWhatsNewService } from '@uigy/modules/overview/kpis/kpi-whats-new/services/kpi-whats-new.service';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { GridUtils } from '@uigy/shared/components/ui-grid/grid-utils';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { NavigationRoutesService } from '@uigy/core/modules/layout/navigation-routes.service';
import {
  FindingsTabQueryParams,
  InventoryTabQueryParams,
  TabGroup,
  TabQueryParams,
} from '@uigy/shared/components/tabs/tabs.enums';
import { VulnerabilityFindingsService } from '@uigy/modules/findings/components/findings-vulnerabilities/services/vulnerability-findings.service';
import { InventoryUiGridService } from '@uigy/modules/inventory/services/inventory-ui-grid.service';
import { UiGridFiltersService } from '@uigy/shared/components/ui-grid/services/ui-grid-filters.service';
import {
  PolicyFindingsService
} from '@uigy/modules/findings/components/findings-policy-violations/services/policy-findings.service';

@Injectable({
  providedIn: 'root'
})
export class KpiWhatsNewICPConfigService {
  private languageService = inject(LanguageService);
  private kpiWhatsNewService = inject(KpiWhatsNewService);
  private navigationRoutesService = inject(NavigationRoutesService);

  private readonly texts = this.languageService.texts.overview.kpi_whats_new_widgets;

  private readonly AssetsDiscovered: WhatsNewKpiConfig<AssetField> = {
    filter: timeFrameValue => this.kpiWhatsNewService.createAssetFilter(timeFrameValue, AssetField.FirstSeen),
    fetchData: filter => this.kpiWhatsNewService.getAssetsCount(filter as AssetExpressionsParams),
    title: this.texts.assets_discovered.text,
    callbackFn: config => {
      if (config.timeFrameValue) {
        const agGridFilter = this.createNavigationTimeFrameFilter(AssetField.FirstSeen, config.timeFrameValue.value);
        return this.navigationRoutesService.navigateWithParams([RoutesStatesNamesEnum.inventory], {
          [TabGroup.InventoryTab]: InventoryTabQueryParams.AllAssets,
          filters: agGridFilter,
          sort: encodeURI(JSON.stringify(InventoryUiGridService.defaultSort)),
        });
      }
    },
    kpiVariant: KpiSmallVariant.Basic,
  };

  private readonly HighRiskViolations: WhatsNewKpiConfig<PolicyFindingFilterField> = {
    filter: timeFrameValue => this.kpiWhatsNewService.createHighRiskViolationFilter(timeFrameValue),
    fetchData: filter => this.kpiWhatsNewService.getHighRiskViolationCount(filter as PolicyFindingsExpressionsParams),
    title: this.texts.high_risk_violations.text,
    kpiVariant: KpiSmallVariant.Basic,
    callbackFn: config => {
      if (config.timeFrameValue) {
        const agGridFilter = GridUtils.createNavigationUrlParams(this.kpiWhatsNewService.createHighRiskViolationFilter(config.timeFrameValue, true));
        this.navigationRoutesService.navigateWithParams([RoutesStatesNamesEnum.risks, RoutesStatesNamesEnum.findings], {
          [TabGroup.FindingsTab]: FindingsTabQueryParams.PolicyViolations,
          filters: agGridFilter,
          sort: encodeURI(JSON.stringify(PolicyFindingsService.defaultSort)),
        });
      }
    }
  };

  private readonly AssetsUpdated: WhatsNewKpiConfig<AssetField> = {
    filter: timeFrameValue => this.kpiWhatsNewService.createAssetFilter(timeFrameValue, AssetField.LastUpdate),
    fetchData: filter => this.kpiWhatsNewService.getAssetsCount(filter as AssetExpressionsParams),
    title: this.texts.assets_updated.text,
    badgeConfig: {label: this.texts.assets_updated.label, icon: IconsEnum.active_query, variant: 'bold-text'},
    callbackFn: config => {
      if (config.timeFrameValue) {
        const agGridFilter = this.createNavigationTimeFrameFilter(AssetField.LastUpdate, config.timeFrameValue.value);
        return this.navigationRoutesService.navigateWithParams([RoutesStatesNamesEnum.inventory], {
          [TabGroup.InventoryTab]: InventoryTabQueryParams.AllAssets,
          filters: agGridFilter,
          sort: encodeURI(JSON.stringify(InventoryUiGridService.defaultSort)),
        });
      }
    },
    kpiVariant: KpiSmallVariant.Basic,
  };

  private readonly OTVulnerabilities: WhatsNewKpiConfig = {
    filter: timeFrameValue => this.kpiWhatsNewService.createFindingsFilter(timeFrameValue, [PluginSource.Tot]),
    fetchData: filter => this.kpiWhatsNewService.getFindingsCount(filter as FindingsObjExpressionsParams),
    title: this.texts.ot_vulnerabilities.text,
    callbackFn: config => {
      if (config.timeFrameValue) {
        const agGridFilter = GridUtils.createNavigationUrlParams(this.kpiWhatsNewService.createFindingsFilter(config.timeFrameValue, [PluginSource.Tot], true));
        this.navigationRoutesService.navigateWithParams([RoutesStatesNamesEnum.risks, RoutesStatesNamesEnum.findings], {
          [TabGroup.FindingsTab]: FindingsTabQueryParams.Vulnerabilities,
          filters: agGridFilter,
          sort: encodeURI(JSON.stringify(VulnerabilityFindingsService.defaultSort)),
        });
      }
    },
    kpiVariant: KpiSmallVariant.Basic,
  };

  private readonly ITVulnerabilities: WhatsNewKpiConfig = {
    filter: timeFrameValue => this.kpiWhatsNewService.createFindingsFilter(timeFrameValue, [PluginSource.Nnm, PluginSource.Nessus]),
    fetchData: filter => this.kpiWhatsNewService.getFindingsCount(filter as FindingsObjExpressionsParams),
    title: this.texts.it_vulnerabilities.text,
    badgeConfig: {label: this.texts.it_vulnerabilities.label, variant: 'bold-text', icon: IconsEnum.nessus_scan},
    callbackFn: config => {
      if (config.timeFrameValue) {
        const agGridFilter = GridUtils.createNavigationUrlParams(this.kpiWhatsNewService.createFindingsFilter(config.timeFrameValue, [PluginSource.Nnm, PluginSource.Nessus], true));
        this.navigationRoutesService.navigateWithParams([RoutesStatesNamesEnum.risks, RoutesStatesNamesEnum.findings], {
          [TabGroup.FindingsTab]: FindingsTabQueryParams.Vulnerabilities,
          filters: agGridFilter,
          sort: encodeURI(JSON.stringify(VulnerabilityFindingsService.defaultSort)),
        });
      }
    },
    kpiVariant: KpiSmallVariant.Basic,
  };

  private readonly OperationalViolations: WhatsNewKpiConfig<PolicyFindingFilterField> = {
    filter: timeFrameValue => this.kpiWhatsNewService.createOperationalViolationsFilter(timeFrameValue),
    fetchData: filter => this.kpiWhatsNewService.getOperationalViolationsCount(filter as PolicyFindingsExpressionsParams),
    title: this.texts.operational_violations.text,
    badgeConfig: {label: this.texts.operational_violations.label, variant: 'bold-text', icon: IconsEnum.code_snapshot},
    kpiVariant: KpiSmallVariant.Basic,
    callbackFn: config => {
      if (config.timeFrameValue) {
        const agGridFilter = GridUtils.createNavigationUrlParams(this.kpiWhatsNewService.createOperationalViolationsFilter(config.timeFrameValue, true));
        this.navigationRoutesService.navigateWithParams([RoutesStatesNamesEnum.risks, RoutesStatesNamesEnum.findings], {
          [TabGroup.FindingsTab]: FindingsTabQueryParams.PolicyViolations,
          filters: agGridFilter,
          sort: encodeURI(JSON.stringify(PolicyFindingsService.defaultSort)),
        });
      }
    }
  };

  public createNavigationTimeFrameFilter(field: AssetField | FindingField, value: TimeFrame): string {
    return UiGridFiltersService.encodeURIFilters(
      JSON.stringify({
        [field]: {
          timeFrame: value,
          filterType: 'greaterEqual',
        },
      }),
    );
  }

  constructor() {
  }

  public getConfigs(): WhatsNewKpiConfig[] {
    return [
      this.AssetsDiscovered,
      this.OTVulnerabilities,
      this.HighRiskViolations,
      this.AssetsUpdated,
      this.ITVulnerabilities,
      this.OperationalViolations,
    ];
  }
}
